import {rotuloDaSituacao, SITUACOES, SituacaoDoLead} from './leads-mia-gestor.component';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {OfertaDoLead} from './oferta-lead-gestor.component';
import {TesteDoLead} from './links-teste-gestor.component';

export interface LeadDoContato {
  id: number;
  telefone: string;
  nome: string;
  empresa: string;
  instagram: string | null;
  email: string | null;
  temComputador: boolean | null;
  origem: string;
  canal: string | null;
  ultimoFormularioEm: string;
  primeiraMensagemEm: string | null;
  testeCriadoEm: string | null;
  testeAcessadoEm: string | null;
  etapa: string;
  etapaMinima: string;
  situacaoConversa: SituacaoDoLead | null;
  /** por que foi marcado como Perdido; null fora de PERDIDO */
  motivoPerda: string | null;
  /** quando o lead deixou claro que não quer mais contato; LEAD = pediu na conversa, EQUIPE = marcado no painel */
  naoQuerContatoEm: string | null;
  naoQuerContatoOrigem: 'LEAD' | 'EQUIPE' | null;
  /** oferta combinada para este lead; tudo null = padrão (R$ 124,90) */
  ofertaValor: number | null;
  ofertaValorInicial: number | null;
  ofertaMesesIniciais: number | null;
}

export interface RespostaLeadContato {
  gestor: boolean;
  numero?: {id: number; telefone: string; modoTeste: boolean; ativadoEm: string | null};
  situacao?: 'telefone_invalido' | 'nao_lead' | 'antes_ativacao' | 'fora_lista' | 'atendendo';
  lead?: LeadDoContato | null;
  /** contato que não veio do site mas tem loja no MeuCardápio; miaAjudando = a Mia já atende como suporte dessa loja */
  loja?: {id: number; nome: string; dominio: string; miaAjudando: boolean} | null;
  atendimento?: {
    id: number; fase: string; rotuloFase: string; encaminhamento: any; ultimaInteracao: string | null; jaFalou?: boolean;
    ficha: {segmento: string | null; ferramentaAtual: string | null; problema: string | null; objecao: string | null; proximoPasso: string | null};
    followup?: FollowupDoLead | null;
  } | null;
  /** links do teste criado pela Mia, acessos do lead e primeiros passos; só depois de criado */
  teste?: TesteDoLead | null;
  /** envios bloqueados para o contato (opt-out automático ou "Bloquear envios"): nada sai pelo número da Gestor */
  bloqueada?: boolean;
}

/** O que a Mia montou pela orientação da equipe, lendo a conversa e o lead (gestor-ia, sem gravar). */
export interface RascunhoDaMia {
  texto: string;
  botoes?: {id: string; titulo: string}[];
  lista?: any;
  link?: any;
  imagem?: string;
}

/** Mensagem do lead ainda sem resposta nossa (equipe ou Mia). */
export interface MensagemDoLead { turnoId: number; texto: string; em: string | null; contatos?: ContatoCompartilhado[] }

/** Contato que o lead compartilhou pelo WhatsApp (vCard), lido para exibição. */
export interface ContatoCompartilhado { nome: string; telefones: string[]; emails: string[] }

/** Lê os blocos BEGIN:VCARD…END:VCARD de uma mensagem; vazio quando ela não traz contato. */
export function leiaContatosVcard(texto: string): ContatoCompartilhado[] {
  const blocos = (texto || '').match(/BEGIN:VCARD[\s\S]*?END:VCARD/gi) || [];
  return blocos.map(bloco => {
    const contato: ContatoCompartilhado = { nome: '', telefones: [], emails: [] };
    let nomeEstruturado = '';
    for (const linha of bloco.replace(/\r?\n[ \t]/g, '').split(/\r?\n/)) {
      const i = linha.indexOf(':');
      if (i < 0) continue;
      const chave = linha.slice(0, i).split(';')[0].replace(/^item\d+\./i, '').toUpperCase();
      const valor = linha.slice(i + 1).trim();
      if (!valor) continue;
      if (chave === 'FN') contato.nome = valor;
      else if (chave === 'N') nomeEstruturado = valor.split(';').filter(p => p.trim()).reverse().join(' ');
      else if (chave === 'TEL') contato.telefones.push(valor);
      else if (chave === 'EMAIL') contato.emails.push(valor);
    }
    contato.nome = contato.nome || nomeEstruturado || 'Contato';
    return contato;
  });
}

/** Follow-up automático (D1, D3, D7) deste atendimento. */
export interface FollowupDoLead {
  ligadoNoNumero: boolean;
  ligado: boolean;
  proximo: {passo: number; agendadoPara: string} | null;
  ultimo: {passo: number; enviadoEm: string | null; respondido: boolean} | null;
}

/** Sugestão do composer: rótulo curto no chip, texto inteiro no tooltip e no campo. Constantes para o ngFor não recriar os chips. */
interface AtalhoDeOrientacao { rotulo: string; texto: string }
const ATALHOS_LEAD_CALADO: AtalhoDeOrientacao[] = [
  {rotulo: 'Ficou dúvida?', texto: 'Pergunta, bem curto, se ficou alguma dúvida ou se ele quer que você mostre um exemplo pronto'},
  {rotulo: 'Oferecer o teste de novo', texto: 'Oferece de novo o teste grátis de 7 dias, sem cobrar resposta, e pergunta se pode montar o cardápio hoje'},
  {rotulo: 'Outro horário?', texto: 'Manda uma mensagem leve perguntando se ainda faz sentido conversar agora ou se prefere outro horário'},
];
const ATALHOS_PEDIU_EQUIPE: AtalhoDeOrientacao[] = [
  {rotulo: 'A equipe já viu', texto: 'Avisa que a equipe viu e vai responder em instantes, sem tentar vender nada agora'},
];
const ATALHOS_CLIENTE: AtalhoDeOrientacao[] = [
  {rotulo: 'Explicar o passo a passo', texto: 'Explica em passos curtos como fazer o que ele pediu no painel, sem vender nada'},
];

/** Cartão "Agora": o que está acontecendo com o contato. A ação principal (no máximo uma) vem de `principal`. */
interface CartaoAgora { tom: string; icone: string; titulo: string; texto: string; citacao?: string; calmo?: string }
interface AcaoPrincipal { tipo: 'reabrir' | 'loja' | 'chamar'; rotulo: string; icone: string; explica: string }

/** Régua do funil (mesmas cores das colunas do kanban); Perdido marca em vermelho onde o lead parou. */
const REGUA = [
  {id: 'NOVO', nome: 'Novo', cor: '#2d5cb8'}, {id: 'CONVERSA', nome: 'Conversa', cor: '#0f7f8c'},
  {id: 'TESTE', nome: 'Teste', cor: '#6d3fc0'}, {id: 'TESTE_ATIVO', nome: 'Ativo', cor: '#4a2ba0'},
  {id: 'CLIENTE', nome: 'Cliente', cor: '#16875a'},
];

const ROTULO_ETAPA: Record<string, string> = {NOVO: 'Novo', CONVERSA: 'Em conversa', TESTE: 'Teste criado', TESTE_ATIVO: 'Teste ativo', CLIENTE: 'Cliente', PERDIDO: 'Perdido'};
const ROTULO_ORIGEM: Record<string, string> = {site_meucardapio: 'MeuCardápio', site_cardapiodigital: 'CardápioDigital', saudacao_site: 'Saudação do site', manual: 'Manual'};

/**
 * Cartão do lead no painel do contato da extensão. Só aparece quando o número pessoal pareado é a
 * Mia Gestor; nesse caso o cartão da Mia da loja some (saída `gestorAtivo`).
 */
@Component({
  selector: 'app-lead-contato-gestor',
  templateUrl: './lead-contato-gestor.component.html',
  styleUrls: ['./lead-contato-gestor.component.scss'],
})
export class LeadContatoGestorComponent implements OnChanges, OnDestroy {
  @Input() telefone: string;
  @Input() nome: string;
  @Output() gestorAtivo = new EventEmitter<boolean>();

  dados: RespostaLeadContato | null = null;
  carregando = false;
  erro = '';
  autorizando = false;
  chamando = false;
  iniciandoTeste = false;
  orientando = false;
  orientacao = '';
  /** prévia montada pela Mia; a equipe edita o texto e decide: copiar ou mandar por ela */
  rascunho: RascunhoDaMia | null = null;
  copiado = false;
  enviando = false;
  /** clique sem orientação: feedback no lugar de botão mudo */
  erroOrientacao = '';
  /** prints colados/anexados na orientação (data URL JPEG já reduzida); a Mia lê como contexto, o lead não vê */
  imagens: string[] = [];
  readonly maxImagens = 3;
  arrastando = false;
  /** arquivo de /upload/imagem que sai para o lead junto da prévia (só pelo Enviar pela Mia); diferente dos prints acima */
  imagemParaOLead: string | null = null;
  /** sugestões geradas pela Mia no botão; null = valem as fixas da situação */
  sugestoesDaMia: AtalhoDeOrientacao[] | null = null;
  sugerindo = false;
  erroSugestoes = '';
  private requisicaoSugestoes = 0;
  enviandoImagemParaOLead = false;
  /** caixa da Mia: recolhida numa linha; abre sozinha quando o lead tem mensagem sem resposta, nunca fecha sozinha */
  comporAberto = false;
  copiadoEmail = false;
  readonly regua = REGUA;
  /** o que o lead mandou desde a nossa última resposta; acompanha a conversa ao lado a cada 5 s */
  ultimasDoLead: MensagemDoLead[] = [];
  copiadoMensagens = false;
  /** último turno do lead que a prévia atual conheceu; mensagens depois dele pedem montar de novo */
  private rascunhoAteTurno: number | null = null;
  private ultimasTimer: any = null;
  /** Logo abaixo do botão: a Mia foi chamada / mandou a mensagem / não mandou. O turno leva até um minuto. */
  acompanhamento: {tom: 'andamento' | 'ok' | 'erro'; texto: string} | null = null;
  private acompanhamentoTimer: any = null;
  movendo = false;
  mudandoFollowup = false;
  mudandoBloqueio = false;
  aviso = '';
  /** campo de motivo aberto (ao marcar Perdido ou ao editar o motivo de um lead já perdido) */
  anotandoPerda = false;
  motivoPerda = '';

  constructor(private http: HttpClient) {}

  ngOnChanges(mudancas: SimpleChanges) {
    if (!mudancas.telefone) return;
    this.requisicaoSugestoes++;
    // outro contato: nada do anterior pode ficar na tela enquanto a consulta volta
    this.dados = null; this.erro = ''; this.aviso = ''; this.anotandoPerda = false; this.motivoPerda = '';
    this.pareAcompanhamento(); this.acompanhamento = null;
    this.pareUltimasDoLead(); this.ultimasDoLead = []; this.rascunho = null; this.rascunhoAteTurno = null; this.imagens = []; this.imagemParaOLead = null;
    this.sugestoesDaMia = null; this.sugerindo = false; this.erroSugestoes = '';
    this.comporAberto = false;
    if (this.telefone) this.carregue();
  }

  ngOnDestroy() { this.requisicaoSugestoes++; this.pareAcompanhamento(); this.pareUltimasDoLead(); }

  private pareUltimasDoLead() {
    if (this.ultimasTimer) { clearInterval(this.ultimasTimer); this.ultimasTimer = null; }
  }

  /**
   * A conversa ao lado não avisa o painel quando o lead escreve; consulta a cada 5 s o que ele mandou desde a
   * nossa última resposta. Assim a rajada fica concatenada aqui, pronta para copiar ou para orientar a Mia por cima.
   */
  private acompanheUltimasDoLead() {
    this.pareUltimasDoLead();
    if (!this.podeCompor) return;
    this.busqueUltimasDoLead();
    this.ultimasTimer = setInterval(() => this.busqueUltimasDoLead(), 5000);
  }

  private busqueUltimasDoLead() {
    if (!this.digitos) { this.pareUltimasDoLead(); return; }
    this.http.get<{mensagens: MensagemDoLead[]}>(`/gestor-ia/lead-contato/${this.digitos}/ultimas-do-lead`).subscribe({
      next: r => {
        this.ultimasDoLead = (r.mensagens || []).map(m => ({...m, contatos: leiaContatosVcard(m.texto)}));
        if (this.ultimasDoLead.length) this.comporAberto = true;
      },
      error: () => { /* próxima tentativa em 5 s */ },
    });
  }

  /** Rajada do lead em texto corrido, uma mensagem por linha: o que o botão Copiar mensagens entrega. */
  get textoDasUltimas(): string {
    return this.ultimasDoLead.map(m => m.contatos?.length
      ? m.contatos.map(c => ['Contato: ' + c.nome, ...c.telefones, ...c.emails].join(' · ')).join('\n')
      : m.texto).join('\n');
  }

  /** Quantas mensagens o lead mandou depois de a prévia atual ter sido montada. */
  get novasDepoisDaPrevia(): number {
    if (!this.rascunho) return 0;
    const ate = this.rascunhoAteTurno || 0;
    return this.ultimasDoLead.filter(m => m.turnoId > ate).length;
  }

  hora(valor: string | null): string {
    const d = valor ? new Date(valor) : null;
    if (!d || isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  copieMensagens() {
    const texto = this.textoDasUltimas.trim();
    if (!texto) return;
    const feito = () => { this.copiadoMensagens = true; setTimeout(() => this.copiadoMensagens = false, 2500); };
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(texto).then(feito, () => this.copiePorCampoTemporario(texto, feito)); }
    else this.copiePorCampoTemporario(texto, feito);
  }

  copieEmail() {
    const texto = String(this.dados?.lead?.email || '').trim();
    if (!texto) return;
    const feito = () => { this.copiadoEmail = true; setTimeout(() => this.copiadoEmail = false, 2000); };
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(texto).then(feito, () => this.copiePorCampoTemporario(texto, feito)); }
    else this.copiePorCampoTemporario(texto, feito);
  }

  abraCompor() {
    this.comporAberto = true;
    setTimeout(() => (document.getElementById('orientacao-mia') as HTMLTextAreaElement | null)?.focus());
  }

  private copiePorCampoTemporario(texto: string, feito: () => void) {
    const campo = document.createElement('textarea');
    campo.value = texto; campo.setAttribute('readonly', ''); campo.style.position = 'fixed'; campo.style.opacity = '0';
    document.body.appendChild(campo); campo.select();
    try { if (document.execCommand('copy')) feito(); } catch (e) { /* sem clipboard */ }
    document.body.removeChild(campo);
  }

  private pareAcompanhamento() {
    if (this.acompanhamentoTimer) { clearInterval(this.acompanhamentoTimer); this.acompanhamentoTimer = null; }
  }

  /**
   * Depois de chamar/orientar: consulta a cada 5 s até a última interação do atendimento mudar (a Mia mandou)
   * ou até 90 s (aí avisa que não saiu). Mantém o feedback colado no botão, em vez de uma faixa longe dele.
   */
  private acompanhe(textoEmAndamento: string, textoOk: string) {
    this.pareAcompanhamento();
    const antes = this.dados?.atendimento?.ultimaInteracao || null;
    const inicio = Date.now();
    this.acompanhamento = {tom: 'andamento', texto: textoEmAndamento};
    this.acompanhamentoTimer = setInterval(() => {
      if (!this.digitos) { this.pareAcompanhamento(); return; }
      this.http.get<RespostaLeadContato>(`/gestor-ia/lead-contato/${this.digitos}`).subscribe({
        next: r => {
          this.dados = r;
          const depois = r.atendimento?.ultimaInteracao || null;
          if (depois && depois !== antes) {
            this.pareAcompanhamento();
            this.acompanhamento = {tom: 'ok', texto: textoOk};
          } else if (Date.now() - inicio > 90000) {
            this.pareAcompanhamento();
            this.acompanhamento = {tom: 'erro', texto: 'Passou mais de um minuto e a mensagem não apareceu no atendimento. Confira o WhatsApp ao lado; se não saiu, chame de novo.'};
          }
        },
        error: () => { /* próxima tentativa em 5 s */ },
      });
    }, 5000);
  }

  get digitos(): string { return String(this.telefone || '').replace(/\D/g, ''); }

  get inicial(): string {
    const n = String(this.dados?.lead?.nome || this.nome || '').trim();
    return n ? n[0].toUpperCase() : 'L';
  }

  get telefoneFormatado(): string { return this.formateTelefone(this.digitos); }

  /** (62) 98104-3475 a partir do wa_id; sem DDI 55, devolve os dígitos como vieram */
  formateTelefone(telefone: string): string {
    const d = String(telefone || '').replace(/\D/g, '');
    const nacional = d.length >= 12 && d.startsWith('55') ? d.slice(2) : d;
    const m = nacional.match(/^(\d{2})(\d{4,5})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : d;
  }

  carregue() {
    if (!this.digitos) return;
    this.carregando = true; this.erro = '';
    this.http.get<RespostaLeadContato>(`/gestor-ia/lead-contato/${this.digitos}`).subscribe({
      next: r => {
        this.carregando = false;
        this.dados = r;
        this.gestorAtivo.emit(!!r.gestor);
        if (r.gestor) this.acompanheUltimasDoLead();
      },
      error: e => {
        this.carregando = false;
        this.dados = null;
        this.erro = e?.error?.erro || 'Não foi possível consultar o lead.';
        this.gestorAtivo.emit(false);
      },
    });
  }

  /**
   * Cartão "Agora". Quem vence: Perdido, Pediu para parar, Pediu a equipe, dono de loja, Cliente, situação da
   * conversa, situação do contato. Pedido da equipe e opt-out vêm do encaminhamento do atendimento, que vale em
   * qualquer etapa (a situação da conversa vem vazia fora de Conversa e Teste).
   */
  get agora(): CartaoAgora {
    const s = this.dados?.situacao;
    if (this.bloqueada) {
      const lead = this.dados?.lead;
      const quando = this.formateData(lead?.naoQuerContatoEm || null);
      const quem = !lead?.naoQuerContatoEm ? '' : lead.naoQuerContatoOrigem === 'LEAD' ? `Pediu na conversa${quando ? ' em ' + quando : ''}. `
        : `Marcado pela equipe${quando ? ' em ' + quando : ''}. `;
      return {tom: 'alerta', icone: 'fa-ban', titulo: 'Não quer mais contato',
        texto: quem + 'Nada sai para este número: a Mia não responde, os toques e a reativação pulam e Chamar a Mia fica travado.',
        calmo: 'Respeite o pedido. Se ele mesmo pedir para voltar, desfaça em Mais ações.'};
    }
    if (this.leadPerdido) {
      const motivo = String(this.dados.lead.motivoPerda || '').trim();
      return {tom: 'alerta', icone: 'fa-times-circle', titulo: 'Lead perdido', texto: 'A Mia não responde nem manda follow-up.',
        citacao: motivo ? `Motivo: ${motivo}` : 'Sem motivo anotado.'};
    }
    if (this.pediuParar) {
      return {tom: 'alerta', icone: 'fa-ban', titulo: 'Pediu para parar', texto: 'O lead pediu para não receber mais mensagens. A Mia não manda nada e os toques foram cancelados.',
        calmo: 'Respeite o pedido. Se ele voltar a escrever, a Mia continua calada.'};
    }
    if (this.pediuEquipe) {
      const ha = this.tempoDesde(this.dados?.atendimento?.encaminhamento?.registradoEm);
      return {tom: 'alerta', icone: 'fa-hand-paper', titulo: `${this.ehCliente ? 'Cliente pediu' : 'Pediu'} a equipe${ha ? ' há ' + ha : ''}`,
        texto: this.ehCliente ? 'Peça o texto abaixo ou responda direto no WhatsApp ao lado.'
          : 'A Mia parou de responder e está esperando você. Peça a mensagem abaixo ou responda direto no WhatsApp ao lado.'};
    }
    // dono de loja em operação: não é lead; a Mia entra (ou já está) como suporte da loja
    if (this.dados?.loja?.miaAjudando) {
      return {tom: 'ok', icone: 'fa-store', titulo: `Mia ajudando na ${this.dados.loja.nome}`, texto: 'Este contato é dono de loja no MeuCardápio. A Mia responde como suporte: impressora, senha, cardápio e painel, com os artigos da central de ajuda. Se travar, ela avisa a equipe.'};
    }
    if (this.dados?.loja) {
      return {tom: 'neutro', icone: 'fa-store', titulo: `Dono da ${this.dados.loja.nome}`, texto: 'Este contato não veio do site: já tem loja no MeuCardápio. Chame a Mia para ajudar na loja, não para vender.'};
    }
    if (this.ehCliente) {
      return {tom: 'ok', icone: 'fa-trophy', titulo: 'Cliente', texto: 'A Mia Gestor não vende mais para ele: sem oferta e sem follow-up.',
        calmo: 'Nada pendente. Se ele pedir a equipe, o aviso aparece aqui.'};
    }
    if (s === 'atendendo') return this.situacaoAtendendo();
    if (s === 'fora_lista') return {tom: 'atencao', icone: 'fa-flask', titulo: 'Fora da lista de teste', texto: 'O número da Mia está em modo teste e este telefone não está na lista.'};
    if (s === 'antes_ativacao') return {tom: 'atencao', icone: 'fa-history', titulo: 'Conversa anterior à ativação', texto: 'Este lead se registrou antes de a Mia assumir o número. Autorize para ela atender.'};
    if (s === 'telefone_invalido') return {tom: 'neutro', icone: 'fa-globe-americas', titulo: 'Sem telefone brasileiro', texto: 'A Mia Gestor só atende celulares brasileiros. Responda você pelo WhatsApp ao lado.'};
    return {tom: 'neutro', icone: 'fa-user', titulo: 'Não veio do site', texto: 'Este contato não preencheu o formulário. A Mia só atende se você autorizar.'};
  }

  /** No máximo uma ação azul no cartão; estado calmo (conversa andando, cliente, pedido da equipe) não tem nenhuma. */
  get principal(): AcaoPrincipal | null {
    if (this.bloqueada) return null;
    if (this.leadPerdido) {
      return {tipo: 'reabrir', rotulo: 'Reabrir o lead', icone: 'fa-undo-alt',
        explica: `Volta para ${this.rotuloEtapa(this.dados.lead.etapaMinima || 'CONVERSA')}; a Mia responde a próxima mensagem.`};
    }
    if (this.pediuParar || this.pediuEquipe) return null;
    if (this.podeChamarParaLoja) {
      return {tipo: 'loja', rotulo: 'Chamar a Mia para ajudar na loja', icone: 'fa-store',
        explica: `Suporte, não venda: a Mia continua esta conversa como ajuda da ${this.dados.loja.nome} (impressora, senha, cardápio, painel). Se travar, ela avisa a equipe.`};
    }
    if (!this.acaoChamar || this.situacaoConversa?.codigo === 'RESPONDENDO') return null;
    return this.acaoChamar;
  }

  /**
   * Chamar a Mia para falar com o lead agora (primeira mensagem, retomada ou chamar a atenção). Vira o botão azul
   * quando é a ação do momento; nos outros estados (conversa andando, pediu a equipe) fica como linha em Mais ações.
   */
  get acaoChamar(): AcaoPrincipal | null {
    // dono de loja: a Mia entra como suporte, nunca como vendedora
    if (this.dados?.loja || !this.podeChamar) return null;
    if (this.leadCalado) {
      return {tipo: 'chamar', rotulo: 'Chamar a atenção do lead', icone: 'fa-bell',
        explica: 'A Mia lê o que mandou e ficou sem resposta e manda algo curto e novo, sem repetir o pedido nem recomeçar.'};
    }
    if (this.miaJaFalou) {
      return {tipo: 'chamar', rotulo: 'Retomar a conversa', icone: 'fa-robot',
        explica: 'Ela lê o que mandou e ficou sem resposta e manda uma mensagem curta com algo novo.'};
    }
    return {tipo: 'chamar', rotulo: 'Chamar a Mia para a conversa', icone: 'fa-robot',
      explica: 'Ela manda a primeira mensagem agora, seguindo de onde a conversa parou.'};
  }

  executePrincipal() {
    const p = this.principal;
    if (!p) return;
    if (p.tipo === 'reabrir') this.reabra();
    else if (p.tipo === 'loja') this.chameMiaParaLoja();
    else this.chameMia();
  }

  get ocupado(): boolean { return this.chamando || this.chamandoLoja || this.autorizando || this.iniciandoTeste || this.movendo; }

  /**
   * "Mia Gestor atendendo" ganha o estado real da conversa (quem está devendo resposta e há quanto tempo),
   * porque é isso que decide o que a equipe faz agora. Sem situação da conversa, fica o texto genérico.
   */
  private situacaoAtendendo(): CartaoAgora {
    const sc = this.situacaoConversa;
    const ha = sc?.tempo ? ` há ${sc.tempo}` : '';
    const generico: CartaoAgora = {tom: 'ok', icone: 'fa-check', titulo: 'Mia Gestor atendendo', texto: 'Este lead está sendo atendido pela Mia no seu número.'};
    if (!sc) {
      if (this.dados?.lead?.etapa !== 'NOVO') return generico;
      const desde = this.tempoDesde(this.dados.lead.ultimoFormularioEm);
      return {tom: 'neutro', icone: 'fa-file-alt', titulo: `Preencheu o formulário${desde ? ' há ' + desde : ''}`,
        texto: 'Ainda não escreveu no WhatsApp. A Mia só responde quando ele escrever, a não ser que você chame.'};
    }
    const proximo = this.followup?.ligado && this.followup.proximo
      ? ` O ${this.rotuloPasso(this.followup.proximo.passo)} sai em ${this.formateData(this.followup.proximo.agendadoPara)}.` : '';
    switch (sc.codigo) {
      case 'RESPONDENDO':
        if (this.dados?.atendimento?.fase === 'CRIAR_TESTE' && !this.dados?.lead?.testeCriadoEm) {
          return {tom: 'ok', icone: 'fa-magic', titulo: 'Mia montando a loja de teste', texto: 'Ela já pulou a qualificação e está pedindo o que falta para criar o teste de 7 dias.',
            calmo: 'Nada a fazer agora. A loja sai assim que ele mandar o que falta.'};
        }
        return {tom: 'ok', icone: 'fa-comments', titulo: 'Conversa em andamento', texto: `O lead falou${ha}. A Mia está respondendo por você no seu número.`,
          calmo: 'Nada a fazer agora. Se quiser mudar o rumo, peça uma mensagem abaixo.'};
      case 'AGUARDANDO_LEAD':
        return {tom: sc.minutos !== null && sc.minutos >= 60 ? 'atencao' : 'neutro', icone: 'fa-hourglass-half', titulo: `Lead sem responder${ha}`,
          texto: 'A Mia perguntou e ele ainda não voltou.' + proximo};
      case 'FOLLOWUP':
        return {tom: 'atencao', icone: 'fa-redo-alt', titulo: `${sc.rotulo} enviado${ha}`, texto: 'Toque automático sem resposta.' + (proximo || ' O próximo já está agendado.')};
      case 'ESFRIOU':
        return {tom: 'frio', icone: 'fa-snowflake', titulo: `Esfriou: sem resposta${ha}`, texto: 'O último toque ficou sem resposta. Não vira Perdido sozinho: tente de novo ou encerre.'};
      default: return generico;
    }
  }

  /** Lead calado depois de a Mia falar: o botão principal vira "chamar a atenção", pelo caminho da retomada. */
  get leadCalado(): boolean {
    const c = this.situacaoConversa?.codigo;
    return this.miaJaFalou && (c === 'AGUARDANDO_LEAD' || c === 'FOLLOWUP' || c === 'ESFRIOU');
  }

  /** Status do encaminhamento do atendimento (SOLICITADO/PENDENTE_PAUSA = pediu a equipe, OPT_OUT = pediu para parar). */
  private get statusEncaminhamento(): string { return String(this.dados?.atendimento?.encaminhamento?.status || ''); }

  /** Pediu para parar: nada de chamar nem orientar; a tela não oferece o que a Mia vai recusar. Vale em qualquer etapa. */
  get pediuParar(): boolean { return this.statusEncaminhamento === 'OPT_OUT' || this.situacaoConversa?.codigo === 'PEDIU_PARAR'; }

  /** Pediu a equipe: vence a etapa (cliente também pede ajuda), por isso lê o encaminhamento e não só a situação da conversa. */
  get pediuEquipe(): boolean {
    const st = this.statusEncaminhamento;
    return st === 'SOLICITADO' || st === 'PENDENTE_PAUSA' || this.situacaoConversa?.codigo === 'PRECISA_HUMANO';
  }

  /** Cliente: a Mia Gestor não vende mais; somem chamar, iniciar teste, oferta e follow-up. */
  get ehCliente(): boolean { return this.dados?.lead?.etapa === 'CLIENTE'; }

  /**
   * Sugestões que preenchem a orientação: rótulo curto no chip, texto inteiro no tooltip e no campo ao clicar
   * (quem não passa o mouse lê no campo). A equipe ajusta o texto se quiser e monta pelo mesmo botão.
   */
  get atalhosDeOrientacao(): AtalhoDeOrientacao[] {
    if (this.sugestoesDaMia) return this.sugestoesDaMia;
    const c = this.situacaoConversa?.codigo;
    if (this.ehCliente) return ATALHOS_CLIENTE;
    if (this.pediuEquipe) return ATALHOS_PEDIU_EQUIPE;
    if (c === 'AGUARDANDO_LEAD' || c === 'FOLLOWUP' || c === 'ESFRIOU') return ATALHOS_LEAD_CALADO;
    return [];
  }

  /** Composer: vale para qualquer contato com telefone, esteja a Mia na conversa ou não; só o opt-out fecha. */
  get podeCompor(): boolean { return !!this.dados && this.dados.situacao !== 'telefone_invalido' && !this.pediuParar && !this.bloqueada; }

  /** Enviar pela Mia só quando ela está no atendimento; fora disso a equipe copia e manda pelo WhatsApp ao lado. */
  get podeEnviarPelaMia(): boolean { return !!this.dados?.atendimento && this.podeChamar; }

  /** Botão "Sugerir com IA": a Mia lê a conversa e as sugestões dela tomam o lugar das fixas até trocar de contato. */
  sugiraOrientacoes() {
    if (this.sugerindo || !this.digitos) return;
    const requisicao = ++this.requisicaoSugestoes;
    this.sugerindo = true; this.erroSugestoes = '';
    this.http.post<{sugestoes: AtalhoDeOrientacao[]}>(`/gestor-ia/lead-contato/${this.digitos}/sugestoes-orientacao`, {}).subscribe({
      next: r => {
        if (requisicao !== this.requisicaoSugestoes) return;
        this.sugerindo = false; this.sugestoesDaMia = r?.sugestoes?.length ? r.sugestoes : this.sugestoesDaMia;
      },
      error: e => {
        if (requisicao !== this.requisicaoSugestoes) return;
        this.sugerindo = false; this.erroSugestoes = e?.error?.erro || 'Não foi possível pedir sugestões à Mia.';
      },
    });
  }

  useAtalho(texto: string) { if (!this.orientando && !this.enviando && !this.chamando) { this.orientacao = texto; this.erroOrientacao = ''; } }

  /** Ctrl+V de um print no campo: vira anexo; texto colado segue o caminho normal. */
  coleImagem(evento: ClipboardEvent) {
    const arquivos = Array.from(evento.clipboardData?.items || [])
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile()).filter(Boolean) as File[];
    if (!arquivos.length) return;
    evento.preventDefault();
    this.anexe(arquivos);
  }

  escolhaImagens(evento: Event) {
    const campo = evento.target as HTMLInputElement;
    this.anexe(Array.from(campo.files || []));
    campo.value = '';
  }

  /** Só reage a arrasto de arquivo; texto arrastado para o campo continua normal. */
  arraste(evento: DragEvent, dentro: boolean) {
    if (!Array.from(evento.dataTransfer?.types || []).includes('Files')) return;
    evento.preventDefault();
    this.arrastando = dentro && !this.orientando && this.imagens.length < this.maxImagens;
  }

  solteImagens(evento: DragEvent) {
    if (!Array.from(evento.dataTransfer?.types || []).includes('Files')) return;
    evento.preventDefault();
    this.arrastando = false;
    if (!this.orientando) this.anexe(Array.from(evento.dataTransfer?.files || []));
  }

  removaImagem(indice: number) { this.imagens.splice(indice, 1); }

  private anexe(arquivos: File[]) {
    const imagens = arquivos.filter(a => a.type.startsWith('image/'));
    const vagas = this.maxImagens - this.imagens.length;
    this.erroOrientacao = !imagens.length ? 'Só dá para anexar imagem (print, foto, JPG, PNG).'
      : imagens.length > vagas ? `Cabem até ${this.maxImagens} imagens por mensagem.` : '';
    for (const arquivo of imagens.slice(0, Math.max(0, vagas))) {
      this.reduza(arquivo).then(
        url => { if (this.imagens.length < this.maxImagens) this.imagens.push(url); },
        () => { this.erroOrientacao = 'Não consegui ler essa imagem. Tente outro print.'; });
    }
  }

  /** Print grande vira JPEG de até 1600 px no lado maior: continua legível para a Mia e fica leve para mandar. */
  private reduza(arquivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject();
      leitor.onload = () => {
        const img = new Image();
        img.onerror = () => reject();
        img.onload = () => {
          const escala = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.naturalWidth * escala));
          canvas.height = Math.max(1, Math.round(img.naturalHeight * escala));
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject();
          ctx.fillStyle = '#fff'; // PNG transparente não vira fundo preto no JPEG
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.src = String(leitor.result || '');
      };
      leitor.readAsDataURL(arquivo);
    });
  }

  get podeAutorizar(): boolean {
    const s = this.dados?.situacao;
    return (s === 'nao_lead' || s === 'antes_ativacao') && !this.leadPerdido;
  }

  autorize() {
    if (this.autorizando || !this.digitos) return;
    this.autorizando = true; this.aviso = ''; this.erro = '';
    this.http.post<{lead: LeadDoContato; acordados: number}>(`/gestor-ia/lead-contato/${this.digitos}/autorizar`, {nome: this.nome || ''}).subscribe({
      next: r => {
        this.autorizando = false;
        this.aviso = r.acordados ? `Autorizado. ${r.acordados} mensagem(ns) recente(s) entregue(s) à Mia.` : 'Autorizado. A Mia responde a partir da próxima mensagem.';
        this.carregue();
      },
      error: e => { this.autorizando = false; this.erro = e?.error?.erro || 'Não foi possível autorizar.'; },
    });
  }

  /** Chamar a Mia: ela manda a primeira mensagem agora e passa a responder. Se já falou, vira retomada. */
  get podeChamar(): boolean {
    const s = this.dados?.situacao;
    return (s === 'nao_lead' || s === 'antes_ativacao' || s === 'atendendo') && !this.pediuParar && !this.leadPerdido && !this.ehCliente && !this.bloqueada;
  }

  /** Perdido: a Mia não atende (o Servidor recusa a mensagem e cancela toques); a tela só oferece reabrir. */
  get leadPerdido(): boolean { return this.dados?.lead?.etapa === 'PERDIDO'; }

  /** Volta ao degrau que os fatos provam (etapa mínima); a Mia passa a responder de novo. */
  reabra() { if (this.dados?.lead) this.mova(this.dados.lead.etapaMinima || 'CONVERSA'); }

  get miaJaFalou(): boolean {
    return this.dados?.situacao === 'atendendo' && !!this.dados?.atendimento?.jaFalou;
  }

  reexecutando = false;

  /** Entrega de novo à Mia o que o lead mandou e ficou sem resposta (ex.: clique engolido enquanto ela estava pausada). */
  reexecutePelaMia() {
    if (this.reexecutando || !this.digitos) return;
    this.reexecutando = true; this.aviso = ''; this.erro = '';
    this.http.post<{reexecutadas: number}>(`/gestor-ia/lead-contato/${this.digitos}/reexecutar`, {}).subscribe({
      next: r => {
        this.reexecutando = false;
        if (r.reexecutadas) this.acompanhe(`${r.reexecutadas} mensagem(ns) entregue(s) à Mia de novo; a resposta sai em até um minuto…`,
          'A Mia respondeu. Veja no WhatsApp ao lado.');
        else this.acompanhamento = {tom: 'erro', texto: 'Nada para reexecutar: a última fala é nossa ou a mensagem é antiga demais.'};
      },
      error: e => { this.reexecutando = false; this.acompanhamento = {tom: 'erro', texto: e?.error?.erro || 'Não foi possível reexecutar pela Mia.'}; },
    });
  }

  chameMia() {
    if (this.chamando || !this.digitos) return;
    this.chamando = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean; retomada?: boolean; lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/chamar-mia`, {nome: this.nome || ''}).subscribe({
      next: r => {
        this.chamando = false;
        this.acompanhe(r.retomada ? 'Mia chamada. Ela está escrevendo a retomada; sai em até um minuto…'
          : 'Mia chamada. Ela está lendo o histórico e escrevendo; a mensagem sai em até um minuto…',
          r.retomada ? 'A Mia mandou a retomada. Veja no WhatsApp ao lado.' : 'A Mia mandou a mensagem. Veja no WhatsApp ao lado.');
      },
      error: e => { this.chamando = false; this.acompanhamento = {tom: 'erro', texto: e?.error?.erro || 'Não foi possível chamar a Mia.'}; },
    });
  }

  /** Dono de loja em operação ainda sem a Mia: o botão certo é suporte, não venda. */
  get podeChamarParaLoja(): boolean {
    return !!this.dados?.loja && !this.dados.loja.miaAjudando && !this.leadPerdido && !this.bloqueada;
  }

  chamandoLoja = false;

  /** A Mia continua a conversa da equipe como suporte da loja (impressora, senha, painel), sem SDR nem teste. */
  chameMiaParaLoja() {
    if (this.chamandoLoja || !this.digitos) return;
    this.chamandoLoja = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean; loja: {id: number; nome: string; dominio: string}}>(`/gestor-ia/lead-contato/${this.digitos}/chamar-mia-loja`, {nome: this.nome || ''}).subscribe({
      next: r => {
        this.chamandoLoja = false;
        this.aviso = `A Mia assumiu como suporte da ${r.loja.nome}: ela lê a conversa e responde em até um minuto. Veja no WhatsApp ao lado.`;
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.chamandoLoja = false; this.erro = e?.error?.erro || 'Não foi possível chamar a Mia para a loja.'; },
    });
  }

  /** Iniciar criação da loja: cabe enquanto a Mia ainda está qualificando (não em CRIAR_TESTE nem com teste criado). */
  get podeIniciarTeste(): boolean {
    const fase = this.dados?.atendimento?.fase;
    return this.podeChamar && this.dados?.situacao === 'atendendo' && !this.dados?.loja
      && !this.dados?.lead?.testeCriadoEm && fase !== 'CRIAR_TESTE' && fase !== 'AUTENTICADO';
  }

  /** A equipe decide pelo lead: a fase vira CRIAR_TESTE e a Mia pula o SPIN, pedindo só o que falta para o teste. */
  inicieTeste() {
    if (this.iniciandoTeste || !this.digitos) return;
    this.iniciandoTeste = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean; lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/iniciar-teste`, {nome: this.nome || ''}).subscribe({
      next: () => {
        this.iniciandoTeste = false;
        this.aviso = 'A Mia vai começar a criação do teste agora: ela pede só o que falta (loja, responsável, e-mail).';
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.iniciandoTeste = false; this.erro = e?.error?.erro || 'Não foi possível iniciar o teste.'; },
    });
  }

  /** Orientar a Mia (composer): a equipe diz como quer a próxima mensagem e ela monta a prévia pela conversa; nada sai. */
  monteRascunho() {
    const orientacao = this.orientacao.trim();
    if (this.orientando || this.enviando || !this.digitos) return;
    if (!orientacao) {
      this.erroOrientacao = 'Escreva primeiro como você quer a mensagem. Ex.: "diga que o teste é gratuito".';
      const campo = document.getElementById('orientacao-mia') as HTMLTextAreaElement | null;
      if (campo) campo.focus();
      return;
    }
    this.erroOrientacao = ''; this.copiado = false;
    this.orientando = true; this.aviso = ''; this.erro = '';
    // a Mia lê a conversa do servidor ao montar: o que o lead mandou até aqui entra; o que chegar depois pede montar de novo
    const ateTurno = this.ultimasDoLead.length ? this.ultimasDoLead[this.ultimasDoLead.length - 1].turnoId : 0;
    this.http.post<{rascunho: RascunhoDaMia}>(`/gestor-ia/lead-contato/${this.digitos}/rascunho`, {orientacao, imagens: this.imagens}).subscribe({
      next: r => { this.orientando = false; this.rascunho = r.rascunho; this.rascunhoAteTurno = ateTurno; },
      error: e => { this.orientando = false; this.erro = e?.error?.erro || 'Não foi possível montar a mensagem.'; },
    });
  }

  /** A equipe aprovou (talvez editou): sai pelo número da Mia e entra no histórico dela. */
  envieRascunho() {
    const rascunho = this.rascunho;
    if (!rascunho || this.enviando || this.orientando || !this.digitos || !rascunho.texto.trim()) return;
    this.enviando = true; this.aviso = ''; this.erro = '';
    this.http.post<{ok: boolean}>(`/gestor-ia/lead-contato/${this.digitos}/enviar-rascunho`,
      {texto: rascunho.texto.trim(), botoes: rascunho.botoes || [], lista: rascunho.lista || null, link: rascunho.link || null,
        imagem: this.imagemParaOLead}).subscribe({
      next: () => {
        this.enviando = false; this.rascunho = null; this.orientacao = ''; this.imagens = []; this.imagemParaOLead = null;
        this.acompanhamento = {tom: 'ok', texto: 'Mensagem enviada pela Mia. Veja no WhatsApp ao lado; ela continua a conversa dali.'};
        setTimeout(() => this.carregue(), 4000);
      },
      error: e => { this.enviando = false; this.erro = e?.error?.erro || 'Não foi possível enviar a mensagem.'; },
    });
  }

  descarteRascunho() {
    if (!this.enviando && !this.orientando) { this.rascunho = null; this.copiado = false; this.rascunhoAteTurno = null; this.imagemParaOLead = null; }
  }

  get urlDaImagemParaOLead(): string { return this.imagemParaOLead ? `/images/empresa/${this.imagemParaOLead}` : ''; }

  /** Imagem que o lead vai ver: sobe como na reativação e sai pelo número da Mia junto da prévia. */
  anexeImagemParaOLead(evento: Event) {
    const campo = evento.target as HTMLInputElement;
    const arquivo = campo.files?.[0];
    campo.value = '';
    if (!arquivo) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(arquivo.type)) { this.erro = 'Use uma imagem JPG, PNG ou WebP.'; return; }
    if (arquivo.size > 5 * 1024 * 1024) { this.erro = 'A imagem pode ter até 5 MB.'; return; }
    const dados = new FormData();
    dados.append('files', arquivo, arquivo.name);
    this.enviandoImagemParaOLead = true; this.erro = '';
    this.http.post<any>('/upload/imagem', dados).subscribe({
      next: r => {
        this.enviandoImagemParaOLead = false; this.imagemParaOLead = r?.data?.file || null;
        if (!this.imagemParaOLead) this.erro = 'Não foi possível subir a imagem.';
      },
      error: () => { this.enviandoImagemParaOLead = false; this.erro = 'Não foi possível subir a imagem.'; },
    });
  }

  /** A equipe manda pelo WhatsApp ao lado: copia o texto (editado ou não) para a área de transferência. */
  copieRascunho() {
    const texto = this.rascunho?.texto.trim();
    if (!texto) return;
    const feito = () => { this.copiado = true; setTimeout(() => this.copiado = false, 2500); };
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(texto).then(feito, () => this.copiePeloCampo(feito)); }
    else this.copiePeloCampo(feito);
  }

  private copiePeloCampo(feito: () => void) {
    const campo = document.getElementById('rascunho-mia') as HTMLTextAreaElement | null;
    if (!campo) return;
    campo.select();
    try { if (document.execCommand('copy')) feito(); } catch (e) { /* sem clipboard: o texto fica selecionado para Ctrl+C */ }
  }

  mova(etapa: string, motivo = '') {
    if (this.movendo || !this.dados?.lead) return;
    this.movendo = true; this.aviso = ''; this.erro = '';
    const jaPerdido = this.dados.lead.etapa === 'PERDIDO';
    this.http.put<{lead: LeadDoContato}>(`/gestor-ia/lead-contato/${this.digitos}/etapa`, {etapa, motivo}).subscribe({
      next: r => {
        this.movendo = false; this.anotandoPerda = false;
        if (this.dados) this.dados.lead = r.lead;
        this.aviso = etapa === 'PERDIDO' && jaPerdido ? 'Motivo da perda anotado.' : `Movido para ${this.rotuloEtapa(etapa)}.`;
      },
      error: e => { this.movendo = false; this.erro = e?.error?.erro || 'Não foi possível mover.'; },
    });
  }

  /** Objeção que a Mia registrou na ficha comercial: vira sugestão de motivo, nunca marcação automática. */
  get objecaoDaFicha(): string { return String(this.dados?.atendimento?.ficha?.objecao || '').trim(); }

  /** Abre o campo de motivo, já sugerindo a objeção da ficha; marcar como Perdido só acontece ao confirmar. */
  abraPerda() {
    this.motivoPerda = this.dados?.lead?.motivoPerda || (this.pediuParar ? 'Pediu para parar' : this.objecaoDaFicha);
    this.anotandoPerda = true; this.aviso = ''; this.erro = '';
  }

  get motivoSugerido(): boolean { return this.anotandoPerda && !this.dados?.lead?.motivoPerda && !!this.objecaoDaFicha && this.motivoPerda === this.objecaoDaFicha; }

  cancelePerda() { this.anotandoPerda = false; this.motivoPerda = ''; }

  confirmePerda() { this.mova('PERDIDO', this.motivoPerda.trim()); }

  // ===== oferta por lead =====
  get ofertaDoLead(): OfertaDoLead {
    const l = this.dados?.lead;
    return {ofertaValor: l?.ofertaValor ?? null, ofertaValorInicial: l?.ofertaValorInicial ?? null, ofertaMesesIniciais: l?.ofertaMesesIniciais ?? null};
  }
  ofertaSalva(lead: LeadDoContato) {
    if (this.dados) this.dados.lead = lead;
    this.erro = ''; this.aviso = 'Oferta salva. A Mia usa essa condição quando falar de preço com este lead.';
  }

  get followup(): FollowupDoLead | null { return this.dados?.atendimento?.followup || null; }

  rotuloPasso(passo: number): string { return ['D1', 'D3', 'D7'][Number(passo) - 1] || `Toque ${passo}`; }

  /** Uma linha para o estado do follow-up; texto vazio quando não há atendimento. */
  get textoFollowup(): string {
    const f = this.followup;
    if (!f) return '';
    if (!f.ligadoNoNumero) return 'Desligado no número (tela de números da Mia Gestor).';
    if (!f.ligado) return 'Desligado para este lead.';
    if (f.proximo) return `Próximo toque ${this.rotuloPasso(f.proximo.passo)} em ${this.formateData(f.proximo.agendadoPara)}.`;
    if (f.ultimo && f.ultimo.respondido) return `Lead respondeu ao toque ${this.rotuloPasso(f.ultimo.passo)}.`;
    if (f.ultimo) return `Toque ${this.rotuloPasso(f.ultimo.passo)} enviado em ${this.formateData(f.ultimo.enviadoEm)}; ciclo encerrado.`;
    return 'Ligado. Agenda na próxima resposta da Mia na conversa comercial.';
  }

  formateData(valor: string | null): string {
    const d = valor ? new Date(valor) : null;
    if (!d || isNaN(d.getTime())) return '';
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${dois(d.getDate())}/${dois(d.getMonth() + 1)} ${dois(d.getHours())}:${dois(d.getMinutes())}`;
  }

  mudeFollowup(ligado: boolean) {
    if (this.mudandoFollowup || !this.digitos) return;
    this.mudandoFollowup = true; this.aviso = ''; this.erro = '';
    this.http.put<{followup: FollowupDoLead}>(`/gestor-ia/lead-contato/${this.digitos}/followup`, {ligado}).subscribe({
      next: r => {
        this.mudandoFollowup = false;
        if (this.dados?.atendimento) this.dados.atendimento.followup = r.followup;
        this.aviso = ligado ? 'Follow-up ligado para este lead.' : 'Follow-up desligado para este lead; toques pendentes cancelados.';
      },
      error: e => { this.mudandoFollowup = false; this.erro = e?.error?.erro || 'Não foi possível alterar o follow-up.'; },
    });
  }

  rotuloEtapa(etapa: string): string { return ROTULO_ETAPA[etapa] || etapa; }

  /** Situação da conversa (quem está devendo resposta), com tom e dica; null fora de CONVERSA/TESTE. */
  get situacaoConversa() {
    const s = this.dados?.lead?.situacaoConversa;
    const def = s ? SITUACOES[s.codigo] : null;
    if (!s || !def) return null;
    const m = s.minutos;
    const tempo = m === null || m === undefined ? '' : m < 60 ? `${Math.max(0, m)} min` : m < 1440 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 1440)} d`;
    return {...def, codigo: s.codigo, minutos: m ?? null, rotulo: rotuloDaSituacao(s), tempo};
  }
  rotuloOrigem(origem: string): string { return ROTULO_ORIGEM[origem] || origem; }

  /** "25 min", "3 h", "2 d" desde a data; vazio sem data. */
  tempoDesde(valor: string | null | undefined): string {
    const d = valor ? new Date(valor) : null;
    if (!d || isNaN(d.getTime())) return '';
    const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    return m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 1440)} d`;
  }

  /** Posição na régua; Perdido marca o degrau que os fatos provam. -1 = fora da régua. */
  get indiceNaRegua(): number {
    const l = this.dados?.lead;
    if (!l) return -1;
    return REGUA.findIndex(e => e.id === (l.etapa === 'PERDIDO' ? (l.etapaMinima || 'CONVERSA') : l.etapa));
  }

  /** Oferta e follow-up: só para lead em venda; somem para cliente, perdido, opt-out e quem não é lead. */
  get mostraAjustes(): boolean {
    const s = this.dados?.situacao;
    return !!this.dados?.lead && (s === 'atendendo' || s === 'antes_ativacao') && !this.ehCliente && !this.leadPerdido && !this.pediuParar && !this.bloqueada;
  }

  /** Ficha comercial ("O que a Mia sabe"); para cliente vira histórico recolhido no fim. */
  get ficha() { return this.dados?.atendimento?.ficha || null; }
  get fichaVazia(): boolean {
    const f = this.ficha;
    return !f || !(f.segmento || f.ferramentaAtual || f.problema || f.objecao || f.proximoPasso);
  }

  /** Esfriou e opt-out: marcar como perdido é a decisão pendente; sobe para o topo das ações. */
  get perdaSugerida(): boolean { return this.pediuParar || this.situacaoConversa?.codigo === 'ESFRIOU'; }

  get podeMarcarPerdido(): boolean { return !!this.dados?.lead && !this.leadPerdido && !this.anotandoPerda; }

  /** Grupo "Mais ações" só aparece com alguma ação real; nada de linha desabilitada. */
  get temMaisAcoes(): boolean {
    return !!this.dados?.lead || this.podeIniciarTeste || this.podeAutorizar || this.dados?.situacao === 'fora_lista' || this.podeMudarBloqueio;
  }

  /** Não quer mais contato: registrado no lead ou envios bloqueados no número (vale também para quem não veio do site). */
  get bloqueada(): boolean { return !!this.dados?.bloqueada || !!this.dados?.lead?.naoQuerContatoEm; }

  get podeMudarBloqueio(): boolean { return !!this.dados?.gestor && this.dados.situacao !== 'telefone_invalido'; }

  /** Não quer mais contato: registra no lead, nada mais sai pelo número da Gestor e os toques pendentes são cancelados. */
  mudeBloqueio(bloqueada: boolean) {
    if (this.mudandoBloqueio || !this.digitos) return;
    if (bloqueada && !confirm('Marcar que este lead não quer mais contato? Fica registrado no lead, a Mia para de responder, os toques e a reativação pulam este número e Chamar a Mia fica travado até desfazer.')) return;
    this.mudandoBloqueio = true; this.aviso = ''; this.erro = '';
    this.http.put<{bloqueada: boolean; lead: LeadDoContato | null}>(`/gestor-ia/lead-contato/${this.digitos}/bloqueio`, {bloqueada}).subscribe({
      next: r => {
        this.mudandoBloqueio = false;
        if (this.dados) { this.dados.bloqueada = r.bloqueada; if (r.lead) this.dados.lead = r.lead; }
        if (r.bloqueada) { this.comporAberto = false; this.rascunho = null; }
        this.aviso = r.bloqueada ? 'Registrado: não quer mais contato. Nada mais sai para este número; toques pendentes cancelados.'
          : 'Desfeito. A Mia não volta sozinha: chame quando quiser falar com ele.';
        this.carregue();
      },
      error: e => { this.mudandoBloqueio = false; this.erro = e?.error?.erro || 'Não foi possível alterar o bloqueio.'; },
    });
  }

  get textoChegada(): string {
    const l = this.dados?.lead;
    if (!l) return '';
    const chegou = this.formateData(l.ultimoFormularioEm);
    const falou = this.formateData(l.primeiraMensagemEm);
    return [chegou ? `Chegou ${chegou}` : '', falou ? `falou ${falou}` : ''].filter(Boolean).join(' · ');
  }

  get textoCanal(): string {
    const l = this.dados?.lead;
    return l ? [l.canal || 'site', this.rotuloOrigem(l.origem)].filter(Boolean).join(' · ') : '';
  }

  get linkKanban(): string { return `/admin/mia-gestor/leads?busca=${encodeURIComponent(this.dados?.lead?.telefone || this.digitos)}`; }

  get linkInstagram(): string | null {
    const i = this.dados?.lead?.instagram;
    if (!i) return null;
    const handle = i.replace(/^@/, '').trim();
    return /^[a-z0-9._]+$/i.test(handle) ? `https://instagram.com/${handle}` : null;
  }
}
